// Seamless-world Phaser-coupled load path.
//
// Pure data + lookups live in apps/game/src/state/regions.ts so unit tests
// can drive them without a DOM. This module owns the IO + Phaser tilemap
// construction.
//
// Module surface:
//   - preloadMegaMap(scene): register mega-map asset loads in scene.load
//     (called from WorldScene.preload). Idempotent if textures already exist.
//   - buildMegaMap(scene): synchronously build the live World from the
//     already-loaded cache entries. Called from create() after preload.
//   - World: live mega-map (extends WorldData with Phaser handles).
//
// Step 2 (earlier commit, STRUCTURAL): wrote a single async loadMegaMap.
// Step 3b (this commit, BEHAVIORAL): split into preload + build so create()
// can synchronously decide between the exterior mega-map path and the
// interior per-map path based on the resumed position.

import Phaser from 'phaser';
import {
  parseWorldData,
  type RawWorldJson,
  type WorldData,
} from '../state/regions';

const WORLD_JSON_KEY = 'world.runtime';
const WORLD_JSON_URL = 'assets/maps/world.runtime.json';
const GROUND_KEY = 'world';
const ABOVE_KEY = 'world-above';
// Same paths the stitcher emits — see packages/emerald-converter/src/
// stitch-exteriors-cli.ts.
const GROUND_URL = 'assets/tilesets/world.atlas.png';
const ABOVE_URL = 'assets/tilesets/world.atlas-above.png';

/** Full live world — WorldData + the Phaser handles built at create-time. */
export interface World extends WorldData {
  tilemap: Phaser.Tilemaps.Tilemap;
  groundLayer: Phaser.Tilemaps.TilemapLayer;
  aboveLayer: Phaser.Tilemaps.TilemapLayer;
  /** Asset paths the world was loaded from — useful for diagnostics. */
  atlasImage: string;
  atlasAboveImage: string;
}

/**
 * Register mega-map JSON + atlas loads on scene.load. Called from
 * WorldScene.preload(). Phaser blocks create() until preload completes,
 * so create() can synchronously call `buildMegaMap` to materialize the
 * live World object.
 */
export function preloadMegaMap(scene: Phaser.Scene): void {
  if (!scene.cache.json.exists(WORLD_JSON_KEY)) {
    scene.load.json(WORLD_JSON_KEY, WORLD_JSON_URL);
  }
  if (!scene.textures.exists(GROUND_KEY)) {
    scene.load.image(GROUND_KEY, GROUND_URL);
  }
  if (!scene.textures.exists(ABOVE_KEY)) {
    scene.load.image(ABOVE_KEY, ABOVE_URL);
  }
}

/**
 * Build the live World from already-loaded cache entries.
 *
 * Atlas tilesets share the existing buildAtlas.ts layout (16×16 content,
 * 1 px margin, 2 px spacing) — `addTilesetImage(key, undefined, 16, 16, 1, 2)`.
 *
 * Both layers start invisible (`setVisible(false)`); `enterExteriorRegion`
 * in WorldScene toggles them on when switching to mega-map mode.
 */
export function buildMegaMap(scene: Phaser.Scene): World {
  const raw = scene.cache.json.get(WORLD_JSON_KEY) as RawWorldJson | undefined;
  if (!raw) {
    throw new Error(`buildMegaMap: ${WORLD_JSON_KEY} not in cache — preloadMegaMap must run first`);
  }
  const data = parseWorldData(raw);

  const tilemap = scene.make.tilemap({
    tileWidth: data.tileSize,
    tileHeight: data.tileSize,
    width: data.worldWidth,
    height: data.worldHeight,
  });
  const groundTileset = tilemap.addTilesetImage(GROUND_KEY, undefined, 16, 16, 1, 2);
  const aboveTileset = tilemap.addTilesetImage(ABOVE_KEY, undefined, 16, 16, 1, 2);
  if (!groundTileset || !aboveTileset) {
    throw new Error('buildMegaMap: addTilesetImage failed for world / world-above');
  }
  const groundLayer = tilemap.createBlankLayer('world-ground', groundTileset, 0, 0);
  const aboveLayer = tilemap.createBlankLayer('world-above', aboveTileset, 0, 0);
  if (!groundLayer || !aboveLayer) {
    throw new Error('buildMegaMap: createBlankLayer failed for world-ground / world-above');
  }

  // Match the per-map render depth convention: ground = 0 (BG), above = 6
  // (BG1, redrawn above the player at depth 5 + NPCs at depth 4).
  groundLayer.setDepth(0);
  aboveLayer.setDepth(6);

  // Stamp per-cell tile indices. Phaser treats index 0 as "no tile" by
  // default, which matches our VOID_INDEX = 0 — explicitly skipping keeps
  // the layer's internal data array sparse.
  for (let y = 0; y < data.worldHeight; y++) {
    for (let x = 0; x < data.worldWidth; x++) {
      const i = y * data.worldWidth + x;
      const g = data.metatileIds[i]!;
      const a = data.metatileIdsAbove[i]!;
      if (g !== 0) groundLayer.putTileAt(g, x, y);
      if (a !== 0) aboveLayer.putTileAt(a, x, y);
    }
  }

  // Mega-map starts invisible; enterExteriorRegion toggles it on.
  groundLayer.setVisible(false);
  aboveLayer.setVisible(false);

  return {
    ...data,
    tilemap,
    groundLayer,
    aboveLayer,
    atlasImage: raw.atlasImage,
    atlasAboveImage: raw.atlasAboveImage,
  };
}
